import React, { useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Hero from "./Hero";
import HomeIntro from "./HomeIntro";
import RegionalGlobe from "./RegionalGlobe";
import StrategicPartners, { ClientsCarousel } from "./StrategicPartners";
import VideoMorphShowcase from "./VideoMorphShowcase";
import CardCustomizer from "./CardCustomizer";
import HandCardShowcase from "./HandCardShowcase";
import {
  getCaptecPerformanceProfile,
  releaseCompletedScrollPin,
  setCaptecPerformanceClass,
} from "./scrollPerformance";
import { createOneTimeViewportAnimation } from "./viewportAnimation";
import "./captec-home.css";
import "./cosmos-stats.css";
import "./regional-globe.css";

gsap.registerPlugin(ScrollTrigger);

export default function CaptecHome() {
  const rootRef = useRef(null);
  const [animationsReady, setAnimationsReady] = useState(false);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const profile = getCaptecPerformanceProfile();
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;

    setCaptecPerformanceClass(root, profile);
    document.documentElement.style.scrollBehavior = "auto";

    ScrollTrigger.config({
      limitCallbacks: true,
      ignoreMobileResize: true,
    });

    gsap.ticker.lagSmoothing(500, 33);
    gsap.ticker.fps(profile.lowResource ? 45 : 60);

    const markReady = () => setAnimationsReady(true);
    window.addEventListener("captec:immersive-hero-complete", markReady);

    const readyFrame = window.requestAnimationFrame(() => {
      const hero = root.querySelector(".immersive-hero");
      if (hero?.classList.contains("is-complete")) markReady();
    });

    return () => {
      window.removeEventListener("captec:immersive-hero-complete", markReady);
      window.cancelAnimationFrame(readyFrame);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      root.classList.remove("is-low-resource", "is-reduced-motion");
      gsap.ticker.fps(60);
    };
  }, []);

  useLayoutEffect(() => {
    if (!animationsReady) return undefined;

    const root = rootRef.current;
    if (!root) return undefined;

    const profile = getCaptecPerformanceProfile();
    const coordinators = [];
    const mm = gsap.matchMedia();
    let cardAmbientObserver = null;
    let cardAmbientVisible = false;
    let resizeTimer = 0;
    let refreshFrame = 0;
    let disposed = false;

    const scheduleRefresh = (retryDelay = null) => {
      if (disposed) return;
      window.clearTimeout(resizeTimer);
      window.cancelAnimationFrame(refreshFrame);
      resizeTimer = window.setTimeout(() => {
        if (disposed) return;

        // Refreshing pin geometry while a section is actively scrubbed can cause
        // a visible hitch. Defer that rare recalculation until the pin is idle.
        if (root.querySelector(".is-scroll-animation-active")) {
          scheduleRefresh(profile.lowResource ? 260 : 180);
          return;
        }

        refreshFrame = window.requestAnimationFrame(() => {
          if (disposed) return;
          ScrollTrigger.refresh();
          ScrollTrigger.update();
        });
      }, retryDelay ?? (profile.lowResource ? 180 : 90));
    };

    const cardStudioSection = root.querySelector(".tarjeta-section");
    const updateCardAmbientState = () => {
      cardStudioSection?.classList.toggle(
        "is-ambient-visible",
        cardAmbientVisible && !document.hidden
      );
    };

    if (cardStudioSection) {
      cardAmbientObserver = new IntersectionObserver(
        ([entry]) => {
          cardAmbientVisible = entry.isIntersecting;
          updateCardAmbientState();
        },
        { rootMargin: "40px 0px", threshold: 0.06 }
      );
      cardAmbientObserver.observe(cardStudioSection);
      document.addEventListener("visibilitychange", updateCardAmbientState);
    }

    const ctx = gsap.context(() => {
      const globeSection = root.querySelector(".regional-globe-section");
      const globeShell = root.querySelector(".regional-globe-shell");
      const globeViewport = root.querySelector(".globe-viewport");
      const globeCopy = root.querySelectorAll(".region-info-card, .regional-copy > *");

      if (globeSection && globeShell && globeViewport) {
        gsap.set(globeShell, {
          y: profile.lowResource ? 24 : 54,
          autoAlpha: 0,
          scale: profile.lowResource ? 0.99 : 0.975,
        });
        gsap.set(globeViewport, {
          autoAlpha: 0,
          scale: profile.lowResource ? 0.98 : 0.93,
          transformOrigin: "50% 50%",
        });
        gsap.set(globeCopy, {
          autoAlpha: 0,
          y: profile.lowResource ? 8 : 16,
        });

        const globeTimeline = gsap.timeline({ paused: true });
        globeTimeline
          .to(globeShell, {
            y: 0,
            autoAlpha: 1,
            scale: 1,
            duration: profile.lowResource ? 0.26 : 0.44,
            ease: "power2.out",
          })
          .to(
            globeViewport,
            {
              autoAlpha: 1,
              scale: 1,
              duration: profile.lowResource ? 0.24 : 0.4,
              ease: "power2.out",
            },
            "-=0.22"
          )
          .to(
            globeCopy,
            {
              autoAlpha: 1,
              y: 0,
              duration: profile.lowResource ? 0.2 : 0.34,
              stagger: profile.lowResource ? 0 : 0.025,
              ease: "power2.out",
            },
            "-=0.26"
          );

        const finishGlobe = () => {
          globeTimeline.progress(1).pause();
          gsap.set(globeShell, { y: 0, autoAlpha: 1, scale: 1 });
          gsap.set(globeViewport, { autoAlpha: 1, scale: 1 });
          gsap.set(globeCopy, { autoAlpha: 1, y: 0 });
          globeSection.classList.add("is-home-reveal-complete");
          globeSection.classList.remove("is-home-reveal-active");
        };

        coordinators.push(
          createOneTimeViewportAnimation({
            target: globeSection,
            preload: profile.lowResource ? 80 : 160,
            fastVelocity: profile.fastScrollVelocity,
            play: (done) => {
              globeSection.classList.add("is-home-reveal-active");
              globeTimeline.eventCallback("onComplete", () => {
                finishGlobe();
                globeSection.classList.remove("is-home-reveal-active");
                done();
              });
              globeTimeline.play(0);
            },
            finish: finishGlobe,
          })
        );
      }

      const partnersSection = root.querySelector(".strategic-partners-section");
      const partnersHeader = partnersSection?.querySelector(".logo-carousel-heading");
      const partnersRail = partnersSection?.querySelector(".partners-marquee");

      if (partnersSection && partnersHeader && partnersRail) {
        gsap.set(partnersHeader, {
          autoAlpha: 0,
          y: profile.lowResource ? 8 : 18,
        });
        gsap.set(partnersRail, {
          autoAlpha: 0,
          y: profile.lowResource ? 8 : 16,
        });

        const partnersTimeline = gsap.timeline({ paused: true });
        partnersTimeline
          .to(partnersHeader, {
            autoAlpha: 1,
            y: 0,
            duration: profile.lowResource ? 0.24 : 0.38,
            ease: "power2.out",
          })
          .to(
            partnersRail,
            {
              autoAlpha: 1,
              y: 0,
              duration: profile.lowResource ? 0.24 : 0.38,
              ease: "power2.out",
            },
            "-=0.18"
          );

        const finishPartners = () => {
          partnersTimeline.progress(1).pause();
          gsap.set([partnersHeader, partnersRail], { autoAlpha: 1, y: 0 });
          partnersSection.classList.add("is-home-reveal-complete");
          partnersSection.classList.remove("is-home-reveal-active");
        };

        coordinators.push(
          createOneTimeViewportAnimation({
            target: partnersSection,
            preload: profile.lowResource ? 60 : 130,
            fastVelocity: profile.fastScrollVelocity,
            play: (done) => {
              partnersSection.classList.add("is-home-reveal-active");
              partnersTimeline.eventCallback("onComplete", () => {
                finishPartners();
                partnersSection.classList.remove("is-home-reveal-active");
                done();
              });
              partnersTimeline.play(0);
            },
            finish: finishPartners,
          })
        );
      }

      mm.add("(min-width: 1100px) and (min-height: 800px) and (prefers-reduced-motion: no-preference)", () => {
        const section = root.querySelector(".tarjeta-section");
        const cardColumn = root.querySelector(".tarjeta-col");
        const controlsColumn = root.querySelector(".controles-col");

        if (!section || !cardColumn || !controlsColumn) return undefined;

        gsap.set(cardColumn, {
          xPercent: profile.lowResource ? -24 : -54,
          autoAlpha: 0,
          rotationY: profile.lowResource ? 0 : 12,
          scale: profile.lowResource ? 0.98 : 0.92,
          transformOrigin: "50% 50%",
        });
        gsap.set(controlsColumn, {
          xPercent: profile.lowResource ? 16 : 30,
          autoAlpha: 0,
        });

        const timeline = gsap.timeline({ paused: true });
        timeline
          .to(cardColumn, {
            xPercent: 0,
            autoAlpha: 1,
            rotationY: 0,
            scale: 1,
            duration: 1,
            ease: "power3.out",
          })
          .to(
            controlsColumn,
            {
              xPercent: 0,
              autoAlpha: 1,
              duration: 0.72,
              ease: "power3.out",
            },
            "-=0.62"
          );

        let maxProgress = 0;
        let completed = false;
        let trigger = null;

        const finishCard = () => {
          if (completed) return;
          completed = true;
          maxProgress = 1;
          timeline.progress(1).pause();
          gsap.set(cardColumn, {
            xPercent: 0,
            autoAlpha: 1,
            rotationY: 0,
            scale: 1,
          });
          gsap.set(controlsColumn, { xPercent: 0, autoAlpha: 1 });
          section.classList.add("is-scroll-animation-complete");
          section.classList.remove("is-scroll-animation-active");
          releaseCompletedScrollPin({ trigger, section });
        };

        trigger = ScrollTrigger.create({
          trigger: section,
          start: "top top",
          end: () =>
            `+=${Math.round(window.innerHeight * (profile.lowResource ? 0.7 : 1.02))}`,
          pin: true,
          pinSpacing: true,
          anticipatePin: 0,
          pinType: "fixed",
          invalidateOnRefresh: true,
          refreshPriority: -15,
          preventOverlaps: "captec-home-pinned",
          onEnter: () => section.classList.add("is-scroll-animation-active"),
          onEnterBack: () => {
            if (completed) finishCard();
          },
          onLeave: finishCard,
          onLeaveBack: () => section.classList.remove("is-scroll-animation-active"),
          onRefresh: () => timeline.progress(maxProgress).pause(),
          onUpdate: (self) => {
            if (self.progress > maxProgress) {
              maxProgress = self.progress;
              timeline.progress(maxProgress).pause();
            }
            if (maxProgress >= 0.999) finishCard();
          },
        });

        return () => {
          trigger?.kill(true);
          timeline.kill();
        };
      });

      mm.add("(max-width: 1099px), (max-height: 799px), (prefers-reduced-motion: reduce)", () => {
        const section = root.querySelector(".tarjeta-section");
        const cardColumn = root.querySelector(".tarjeta-col");
        const controlsColumn = root.querySelector(".controles-col");
        if (!section || !cardColumn || !controlsColumn) return undefined;

        gsap.set([cardColumn, controlsColumn], { y: 22, autoAlpha: 0 });
        const timeline = gsap.timeline({ paused: true });
        timeline
          .to(cardColumn, {
            y: 0,
            autoAlpha: 1,
            duration: 0.32,
            ease: "power2.out",
          })
          .to(
            controlsColumn,
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.32,
              ease: "power2.out",
            },
            "-=0.14"
          );

        const finish = () => {
          timeline.progress(1).pause();
          gsap.set([cardColumn, controlsColumn], { y: 0, autoAlpha: 1 });
          section.classList.add("is-scroll-animation-complete");
        };

        const coordinator = createOneTimeViewportAnimation({
          target: section,
          preload: 80,
          fastVelocity: profile.fastScrollVelocity,
          play: (done) => {
            timeline.eventCallback("onComplete", () => {
              finish();
              done();
            });
            timeline.play(0);
          },
          finish,
        });
        coordinators.push(coordinator);

        return () => timeline.kill();
      });

    }, root);

    scheduleRefresh();
    document.fonts?.ready?.then(scheduleRefresh);
    window.addEventListener("resize", scheduleRefresh, { passive: true });
    window.addEventListener("captec:scroll-pin-released", scheduleRefresh);

    return () => {
      disposed = true;
      window.removeEventListener("resize", scheduleRefresh);
      window.removeEventListener("captec:scroll-pin-released", scheduleRefresh);
      window.clearTimeout(resizeTimer);
      window.cancelAnimationFrame(refreshFrame);
      coordinators.forEach((coordinator) => coordinator.destroy());
      cardAmbientObserver?.disconnect();
      document.removeEventListener("visibilitychange", updateCardAmbientState);
      cardStudioSection?.classList.remove("is-ambient-visible");
      mm.revert();
      ctx.revert();
    };
  }, [animationsReady]);

  return (
    <div ref={rootRef} className="captec-home">
      <HomeIntro />
      <Hero />

      <StrategicPartners />

      <section className="regional-globe-section" id="nosotros-home">
        <RegionalGlobe />
      </section>

      <VideoMorphShowcase enabled={animationsReady} />

      <ClientsCarousel />

      <section className="tarjeta-section" id="servicios-home">
        <CardCustomizer />
      </section>

      <HandCardShowcase enabled={animationsReady} />
    </div>
  );
}
